import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/primitives";
import { Coins, ArrowClockwise, GasPump, Wrench, Users, Mountains } from "@phosphor-icons/react";

function fmtK(n, digits = 2) {
  if (n === null || n === undefined) return "—";
  return `K ${Number(n).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

const RATE_META = [
  { key: "cost_per_tonne_zmw",       label: "Cost per tonne",        icon: Mountains, color: "#059669" },
  { key: "cost_per_machine_hour_zmw",label: "Cost per machine-hour", icon: Wrench,   color: "#7c3aed" },
  { key: "fuel_per_tonne_zmw",       label: "Fuel per tonne",        icon: GasPump,  color: "#d97706" },
  { key: "litres_per_tonne",         label: "Litres per tonne",      icon: GasPump,  color: "#0891b2", suffix: " L" },
];

export default function CostIntelligence() {
  const [since, setSince] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [until, setUntil] = useState(new Date().toISOString().slice(0, 10));
  const [summary, setSummary] = useState(null);
  const [perAsset, setPerAsset] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        api.get(`/cost/summary?since=${since}&until=${until}`),
        api.get(`/cost/per-machine?since=${since}&until=${until}`),
      ]);
      setSummary(s.data);
      setPerAsset(m.data.rows || []);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="cost-intel-page">
      <PageHeader
        overline="Finance / Intelligence"
        title="Cost Intelligence"
        subtitle="Every tonne, every hour — the real unit economics behind your operation."
        testId="cost-header"
      />

      <div className="phizo-card p-4 mb-4 flex items-center gap-3 flex-wrap">
        <Coins size={16} weight="bold" className="text-[#71717a]" />
        <label className="text-xs flex items-center gap-2">
          <span className="phizo-label text-[10px]">From</span>
          <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
                 className="border border-[#e4e4e7] px-3 py-1.5 text-xs outline-none focus:border-black"
                 data-testid="cost-since"/>
        </label>
        <label className="text-xs flex items-center gap-2">
          <span className="phizo-label text-[10px]">To</span>
          <input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
                 className="border border-[#e4e4e7] px-3 py-1.5 text-xs outline-none focus:border-black"
                 data-testid="cost-until"/>
        </label>
        <button onClick={load} data-testid="cost-run"
                className="border border-[#e4e4e7] px-3 py-1.5 text-xs hover:bg-[#f4f4f5]">
          <ArrowClockwise size={12} weight="bold" className="inline mr-1" /> Run
        </button>
      </div>

      {loading && !summary && <div className="phizo-card p-6 text-center text-[#71717a]">Loading…</div>}

      {summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {RATE_META.map(({ key, label, icon: Icon, color, suffix }) => {
              const val = summary.rates[key];
              return (
                <div key={key} className="phizo-card p-4" data-testid={`cost-rate-${key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="phizo-label text-[10px]">{label}</div>
                    <Icon size={14} weight="bold" style={{ color }} />
                  </div>
                  <div className="mono text-3xl font-black tracking-tighter" style={{ color }}>
                    {val === null || val === undefined ? "—"
                      : suffix ? Number(val).toFixed(4) + suffix : `K ${Number(val).toFixed(2)}`}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="phizo-card p-5">
              <h3 className="phizo-label text-[10px] mb-3">Cost drivers</h3>
              <table className="w-full text-xs">
                <tbody>
                  <tr className="border-b border-[#f4f4f5]">
                    <td className="py-2"><GasPump size={12} weight="bold" className="inline text-[#d97706] mr-1"/> Fuel & Diesel</td>
                    <td className="py-2 text-right mono">{fmtK(summary.costs.fuel_zmw)}</td>
                    <td className="py-2 text-right mono text-[10px] text-[#71717a]">{summary.costs.fuel_litres} L</td>
                  </tr>
                  <tr className="border-b border-[#f4f4f5]">
                    <td className="py-2"><Wrench size={12} weight="bold" className="inline text-[#b45309] mr-1"/> Repairs & Maintenance</td>
                    <td className="py-2 text-right mono">{fmtK(summary.costs.maintenance_zmw)}</td>
                    <td></td>
                  </tr>
                  <tr className="border-b border-[#f4f4f5]">
                    <td className="py-2"><Users size={12} weight="bold" className="inline text-[#7c3aed] mr-1"/> Payroll (Gross)</td>
                    <td className="py-2 text-right mono">{fmtK(summary.costs.payroll_zmw)}</td>
                    <td></td>
                  </tr>
                  <tr className="border-t-2 border-[#e4e4e7]">
                    <td className="py-2 font-bold uppercase text-[10px] tracking-wider">Total</td>
                    <td className="py-2 text-right mono font-bold">{fmtK(summary.costs.total_zmw)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="phizo-card p-5">
              <h3 className="phizo-label text-[10px] mb-3">Volume</h3>
              <div className="space-y-3">
                <div>
                  <div className="mono text-3xl font-black">{summary.tonnes.toLocaleString()} <span className="text-sm text-[#71717a]">t</span></div>
                  <div className="phizo-label text-[10px]">Tonnes produced · {summary.days_recorded} days</div>
                </div>
                <div>
                  <div className="mono text-2xl font-black">{summary.machine_hours.toLocaleString()} <span className="text-sm text-[#71717a]">hrs</span></div>
                  <div className="phizo-label text-[10px]">Machine hours logged</div>
                </div>
              </div>
            </div>
          </div>

          <div className="phizo-card overflow-x-auto">
            <div className="px-4 py-3 border-b border-[#e4e4e7]">
              <h3 className="phizo-label text-[10px]">Per-asset breakdown</h3>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
                <tr className="text-left">
                  <th className="px-3 py-2 phizo-label text-[9px]">Asset</th>
                  <th className="px-3 py-2 phizo-label text-[9px]">Kind</th>
                  <th className="px-3 py-2 phizo-label text-[9px] text-right">Fuel</th>
                  <th className="px-3 py-2 phizo-label text-[9px] text-right">Litres</th>
                  <th className="px-3 py-2 phizo-label text-[9px] text-right">Maint.</th>
                  <th className="px-3 py-2 phizo-label text-[9px] text-right">Hours</th>
                  <th className="px-3 py-2 phizo-label text-[9px] text-right">Total</th>
                  <th className="px-3 py-2 phizo-label text-[9px] text-right">Cost/hr</th>
                </tr>
              </thead>
              <tbody>
                {perAsset.filter((a) => a.total_zmw > 0 || a.hours > 0).map((a) => (
                  <tr key={a.id} className="border-b border-[#f4f4f5] hover:bg-[#fafafa]"
                      data-testid={`cost-asset-${a.asset_tag || a.id.slice(0,6)}`}>
                    <td className="px-3 py-2 font-semibold">{a.name}</td>
                    <td className="px-3 py-2 mono text-[10px] text-[#71717a]">{a.kind}</td>
                    <td className="px-3 py-2 text-right mono">{fmtK(a.fuel_zmw)}</td>
                    <td className="px-3 py-2 text-right mono text-[#71717a]">{a.litres}</td>
                    <td className="px-3 py-2 text-right mono">{fmtK(a.maint_zmw)}</td>
                    <td className="px-3 py-2 text-right mono">{a.hours}</td>
                    <td className="px-3 py-2 text-right mono font-bold">{fmtK(a.total_zmw)}</td>
                    <td className="px-3 py-2 text-right mono">{fmtK(a.cost_per_hour_zmw)}</td>
                  </tr>
                ))}
                {perAsset.filter((a) => a.total_zmw > 0 || a.hours > 0).length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-[#71717a]">No cost activity in this window.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
